import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Train, Mail, Lock, User, Eye, EyeOff, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login({ setUser }) {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    totpCode: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        // Register
        const { data } = await axios.post(
          `${API}/api/auth/register`,
          {
            email: formData.email,
            password: formData.password,
            name: formData.name
          },
          { withCredentials: true }
        );
        setUser(data);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        // Login
        const { data } = await axios.post(
          `${API}/api/auth/login`,
          {
            email: formData.email,
            password: formData.password,
            totp_code: formData.totpCode || null
          },
          { withCredentials: true }
        );

        if (data.requires_2fa) {
          setRequires2FA(true);
          toast.info('Please enter your 2FA code');
        } else {
          setUser(data);
          toast.success('Welcome back!');
          navigate('/dashboard');
        }
      }
    } catch (e) {
      const errorMsg = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/google/login`);
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error('Google login not available. Please use email/password.');
    }
  };

  const handleDemoLogin = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/demo`);
      setUser(data);
      toast.success('Welcome to Demo Mode!');
      navigate('/dashboard');
    } catch (e) {
      toast.error('Demo login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Train className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
              RailSync
            </h1>
          </div>
          <p className="text-zinc-400">Track your train journeys effortlessly</p>
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-xl text-white">
              {requires2FA ? 'Two-Factor Authentication' : isRegister ? 'Create Account' : 'Welcome Back'}
            </CardTitle>
            <CardDescription className="text-zinc-400">
              {requires2FA 
                ? 'Enter the code from your authenticator app'
                : isRegister 
                  ? 'Sign up to start tracking your PNRs' 
                  : 'Sign in to your account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {requires2FA ? (
                /* 2FA Code Input */
                <div className="space-y-2">
                  <Label htmlFor="totpCode" className="text-zinc-300">
                    <Shield className="w-4 h-4 inline mr-2" />
                    Authenticator Code
                  </Label>
                  <Input
                    id="totpCode"
                    name="totpCode"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={formData.totpCode}
                    onChange={handleChange}
                    className="bg-zinc-800 border-zinc-700 text-white text-center text-2xl tracking-widest"
                    maxLength={6}
                    autoFocus
                    required
                  />
                </div>
              ) : (
                <>
                  {isRegister && (
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-zinc-300">
                        <User className="w-4 h-4 inline mr-2" />
                        Name
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleChange}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        required
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-300">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-zinc-300">
                      <Lock className="w-4 h-4 inline mr-2" />
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        className="bg-zinc-800 border-zinc-700 text-white pr-10"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
              >
                {loading ? 'Please wait...' : requires2FA ? 'Verify' : isRegister ? 'Create Account' : 'Sign In'}
              </Button>

              {requires2FA && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-zinc-400"
                  onClick={() => {
                    setRequires2FA(false);
                    setFormData({ ...formData, totpCode: '' });
                  }}
                >
                  Back to Login
                </Button>
              )}
            </form>

            {!requires2FA && (
              <>
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-zinc-800"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-zinc-900 text-zinc-500">or continue with</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    onClick={handleGoogleLogin}
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-zinc-400 hover:text-white hover:bg-zinc-800"
                    onClick={handleDemoLogin}
                  >
                    Try Demo (No Login Required)
                  </Button>
                </div>

                <p className="text-center text-sm text-zinc-400 mt-6">
                  {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
                  <button
                    type="button"
                    onClick={() => setIsRegister(!isRegister)}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    {isRegister ? 'Sign in' : 'Sign up'}
                  </button>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
